
import { CoreV1Api, dumpYaml, HttpError, V1Pod } from '@kubernetes/client-node'
import * as chalk from 'chalk'
import { Command } from 'commander'
import { PassThrough } from 'stream'
import KubernetesOperatorClient from 'yam-operator/kube/kube-client'
import { createLogger, timeOffSet } from 'yam-operator/util'
import { engineSettings, persistSettings } from '../ops/engine'
import { checkKubeConfAndContext, createKubeClient4Cmd } from '../ops/kube'
const log = createLogger('cli')

export function addKubernetesCommands(program: Command): void {

  program.command('ns').argument('[namespace]', 'Switch the default namespace').action((ns) => {
    if (!ns) {
      console.log(`current namespace is: ${chalk.yellow(engineSettings.clientConf.currentNamespace || 'not set')}`)
      return
    }
    engineSettings.clientConf.currentNamespace = ns
    persistSettings()
    console.log(`current namespace is: ${ns}`)
  })

  program.command('ctx').argument('[context]', 'Switch the default kube context').action(async (ctx) => {
    if (!ctx) {
      const { rawCtx } = await checkKubeConfAndContext()
      console.log(`current kube context is: ${chalk.yellow(rawCtx || 'not set')}, current namespace is: ${chalk.yellow(engineSettings.clientConf.currentNamespace || 'not set')}`)
      return
    }
    engineSettings.clientConf.currentContext = ctx
    persistSettings()
    console.log(`current namespace is: ${ctx}`)
  })

  program.command('status').argument('[workload]', "The application name")
    .option('--context [context]', 'The kubernetes context running on')
    .option('-d, --dir [dir]', 'Directory of the application model')
    .option('-v, --verbose [verbose]', 'Show yaml output and verbose info of all related resources')
    .action(async (workload, { context, verbose, dir }) => {
      const { kubeClient, namespace, app } = await createKubeClient4Cmd(workload, context, dir)

      const pods = await getAllPodByNameOrLabel(kubeClient, app, namespace)
      console.log('Pods ----------------------------------------------------------------------------')
      if (verbose) {
        console.log(dumpYaml(pods))
      } else {
        console.table(pods.map(p => {
          return {
            pod: p.metadata?.name,
            phase: p.status?.phase,
            restarts: p.status?.containerStatuses?.[0].restartCount,
            ready: p.status?.containerStatuses?.[0].ready,
            containers: p.spec?.containers.map(i => i.name).join(', '),
            images: p.spec?.containers.map(i => i.image?.indexOf('/') === -1 ? i.image : i.image?.substring(i.image?.indexOf('/') + 1)).join(','),
            ip: p.status?.podIP,
            node: p.status?.hostIP,
            startTime: p.status?.startTime
          }
        }))
      }

      console.log('Recent Events --------------------------------------------------------------------')
      try {
        const apiClient = kubeClient.makeApiClient(CoreV1Api)
        const workloadEventsRes = await apiClient.listNamespacedEvent(namespace)
        console.table(workloadEventsRes.body.items
          .filter(e => e.involvedObject.name?.includes(workload))
          .sort((a, b) => (b.lastTimestamp?.valueOf() || 0) - (a.lastTimestamp?.valueOf() || 0))
          .map(e => {
            return {
              object: e.involvedObject.kind + '/' + e.involvedObject.name,
              type: e.type, // e.type === "Warning" ? chalk.yellow(e.type) : e.type === "Error" ? chalk.red(e.type) : e.type,
              reason: e.reason,
              message: e.message?.substring(0, 80),
              count: e.count,
              age: timeOffSet(e.metadata.creationTimestamp),
              lastSeen: timeOffSet(e.lastTimestamp),
            }
          }))
      } catch (ex) {
        if (ex instanceof HttpError) {
          console.error(`status: ${ex.statusCode}, msg: ${ex.message}`)
        }
      }

    })

  program.command('forward').argument('[workload]', "The application name, or pod name")
    .option('-p, --from-to [fromTo]', 'The port needs to be forwarded')
    .option('-d, --dir [dir]', 'Directory of the application model')
    .option('--check [checkStatus]', 'Retry forwarding or not when failed')
    .option('--context [context]', 'The kubernetes context running on')
    .action(async (workload, args) => {
      const { fromTo, checkStatus, context, dir } = args
      const { kubeClient, namespace, app } = await createKubeClient4Cmd(workload, context, dir, true)
      const pods = await kubeClient.getPods({
        podName: app,
        namespace
      })
      const specifiedPod = pods.length > 0
      const port = fromTo as string
      let from
      let to
      if (!fromTo) {
        console.error('The port parameter (-p or --from-to) is not set')
        return
      }
      if (port.indexOf(':') === -1) {
        from = parseInt(port)
        to = parseInt(port)
      } else {
        const tmp = port.split(':')
        from = parseInt(tmp[0])
        to = parseInt(tmp[1])
      }
      if (!from) {
        console.error('Invalid container port')
        return
      }
      await kubeClient.portForward({
        podName: specifiedPod ? pods[0].metadata?.name : undefined,
        labelSelectors: specifiedPod ? undefined : { app },
        maxCheckTimes: checkStatus ? Number.MAX_SAFE_INTEGER : undefined,
        containerPort: from,
        targetPort: to,
        namespace,
        bindAll: true
      })
    })

  program.command('exec').argument('[workload]', "The application name")
    .option('-c, --container [container]', 'The container name')
    .option('-d, --dir [dir]', 'Directory of the application model')
    .option('--context [context]', 'The kubernetes context running on')
    .action(async (workload, { container, context, dir }) => {
      const { kubeClient, namespace, app } = await createKubeClient4Cmd(workload, context, dir)
      const pod = await getPodByNameOrLabel(kubeClient, app, namespace)
      if (!pod) {
        console.error(`pod not found, exit`)
        return
      }
      const readable = new PassThrough()
      process.stdin.setRawMode(true)
      process.stdin.resume()
      process.stdin.setEncoding('utf8')
      process.stdin.on('data', (key) => {
        const k = key.toString()
        if (k === '\u0003' || k === "\u0011") {
          readable.write(k)
          return
        }
        if (k === '\r') {
          readable.write('\n')
        } else {
          readable.write(k)
        }
      })
      await kubeClient.exec({
        podName: pod.metadata?.name,
        stdin: readable,
        onExit: (code, reason) => {
          console.log(`Exited. ${reason}`)
          process.exit(code)
        },
        container: container || pod.spec?.containers[0].name
      })
    })

  program.command('logs').argument('[workload]', "The application name")
    .option('-c, --container [container]', 'The container name')
    .option('-d, --dir [dir]', 'Directory of the application model')
    .option('--recent [recent]', "Same as kubectl log --recent")
    .option('-l, --limit [limit]', "Same as kubectl log --limit, defaults to 1024 lines")
    .option('-f, --follow [follow]', "Follow logs, same as kubectl log -f")
    .option('-p, --previous [previous]', "Fetch previous container logs")
    .option('--context [context]', 'The kubernetes context running on')
    .action(async (workload, { container, previous, recent, limit, follow, context, dir }) => {
      const { kubeClient, namespace, app } = await createKubeClient4Cmd(workload, context, dir)
      const pod = await getPodByNameOrLabel(kubeClient, app, namespace)
      if (!pod) {
        return
      }
      kubeClient.enableLog()
      console.log('---------------------------------------- Logs -----------------------------------------')
      const logs = await kubeClient.fetchLogs({
        pod: pod.metadata?.name || '',
        limit,
        followAndPipeTo: follow ? process.stdout : undefined,
        containers: [container],
        tail: true,
        previous,
        namespace,
        sinceSeconds: recent
      })
      if (logs) {
        console.log(logs.join('\n'))
      }
    })
}

async function getPodByNameOrLabel(kubeClient: KubernetesOperatorClient, app: string, namespace: string): Promise<V1Pod | undefined> {
  const pods = await getAllPodByNameOrLabel(kubeClient, app, namespace)
  return pods[0]
}

async function getAllPodByNameOrLabel(kubeClient: KubernetesOperatorClient, app: string, namespace: string): Promise<V1Pod[]> {
  let pods = await kubeClient.getPods({
    podName: app,
    namespace
  })
  const specifiedPod = pods.length > 0
  if (!specifiedPod) {
    pods = await kubeClient.getPods({ labelSelectors: { app }, namespace })
    if (pods.length === 0) {
      log.error(`target pod not found`)
      return []
    }
  }
  return pods
}
